import {useId} from 'react';
import {useSelector} from 'react-redux';

import AemButton from '@/components/AemButton/AemButton';
import BreakdownList from '@/components/BreakdownList/BreakdownList';
import Drawer from '@/components/Drawer/Drawer';
import MetricBar from '@/components/MetricBar/MetricBar';
import RichText from '@/components/RichText/RichText';
import ScoreBar from '@/components/ScoreBar/ScoreBar';
import {Text} from '@/components/Typography';
import useStickyWhileOpen from '@/hooks/useStickyWhileOpen';
import {getContent} from '@/services/content/content';
import buttonActionService from '@/services/experienceScore/buttonActionService';
import metricMapping from '@/services/experienceScore/mappings/metricMapping';
import {selectExperienceInsightsAndTips} from '@/state/selectors/inference';

import ExplanationCard from './ExplanationCard';

interface ExperienceScoreInfoModalProps {
    open: boolean;
    /** Set when the drawer explains one metric instead of the whole score. */
    code?: string;
    onClose: () => void;
}

const ExperienceScoreInfoModal = ({open, code, onClose}: ExperienceScoreInfoModalProps) => {
    const titleId = useId();
    const insightsAndTips = useSelector(selectExperienceInsightsAndTips);
    // Keeps the drawer's content rendered while it animates closed, instead of going blank instantly.
    const stickyCode = useStickyWhileOpen(code, open);
    const metric = stickyCode ? metricMapping.getMetric(stickyCode) : undefined;
    const experienceScoreContent = getContent().experienceScoreContent;
    const {title, description, progressBar, breakdownTitle, button, cardTitle, cardImage} =
        metricMapping.getExplanation(stickyCode) ?? {};
    const rows = metricMapping.getBreakdownRows(metric, stickyCode ? insightsAndTips?.[stickyCode] : undefined);
    const learnMoreLabel = experienceScoreContent?.learnMoreLabel;

    return (
        <Drawer open={open} onClose={onClose} closeLabel={title} aria-labelledby={titleId}>
            {progressBar &&
                (stickyCode ? (
                    <MetricBar progressBar={progressBar} value={progressBar.staticValue ?? 0} labelledBy={titleId} />
                ) : (
                    <ScoreBar value={progressBar.staticValue ?? 0} labelledBy={titleId} />
                ))}
            {(title || description) && (
                <div className='flex w-full flex-col gap-2 text-left'>
                    <Text id={titleId} as='h2' variant='drawerTitle'>
                        {title}
                    </Text>
                    <RichText as='p' html={description} variant='body' />
                </div>
            )}
            {rows.length > 0 && (
                <div className='flex w-full flex-col gap-4 text-left'>
                    <Text as='h3' variant='sectionTitle'>
                        {breakdownTitle}
                    </Text>
                    <BreakdownList
                        rows={rows}
                        learnMoreLabel={learnMoreLabel}
                        onRowButtonClick={(row) => buttonActionService.run(row.button, stickyCode)}
                    />
                </div>
            )}
            {cardImage ? (
                <ExplanationCard image={cardImage} title={cardTitle} button={button} code={stickyCode} />
            ) : (
                <AemButton button={button} code={stickyCode} />
            )}
        </Drawer>
    );
};

export default ExperienceScoreInfoModal;
